/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Image Input Component
 *
 * A self-contained form field component that displays a thumbnail of the
 * currently stored image value and opens the ImagePickerModalComponent gallery
 * when the user clicks the button. It works uniformly for top-level fields
 * (rendered by FormBuilderComponent) and for fields inside a list-of-objects
 * (rendered by InputListComponent).
 *
 * Key Features:
 * - Displays a live thumbnail of the stored base64 image
 * - "Select Image" button opens the image picker modal gallery
 * - Writes the chosen image back through the standard form-field save pipeline
 *   (control.setValue → valueChanges → onControlValueChanged → saveFunction)
 *
 * Related Modules:
 * - FormBuilderComponent — renders this for top-level fields with an imagePicker UI component
 * - InputListComponent — renders this for fields inside list-of-objects
 * - ImagePickerModalComponent — the modal gallery opened on button click
 *
 * Implementation Notes:
 * - Extends BaseFormFieldDirective for consistent Angular form control integration
 * - Angular's built-in URL sanitizer natively allows `data:image/...;base64,...` URIs on `<img [src]>`,
 *   so no DomSanitizer bypass is required
 * - The images input receives the selectable images from the schema's ui:components entry
 */

import {Component, inject, Input, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {IxModule, ModalService} from '@siemens/ix-angular';
import {TranslateModule} from '@ngx-translate/core';
import {ReactiveFormsModule} from '@angular/forms';

import {BaseFormFieldDirective} from '../../base-form-field.directive';
import {FieldLabelComponent} from '../field-label/field-label.component';
import {ImagePickerModalComponent} from '../../../../../../shared/modals/image-picker-modal/image-picker-modal.component';

@Component({
  selector: 'app-image-input',
  standalone: true,
  imports: [
    CommonModule,
    IxModule,
    TranslateModule,
    ReactiveFormsModule,
    FieldLabelComponent,
  ],
  templateUrl: './image-input.component.html',
  styleUrl: '../input-components.scss',
})
export class ImageInputComponent
  extends BaseFormFieldDirective
  implements OnInit
{
  /** Selectable images configured via the schema's `ui:components` imagePicker entry. */
  @Input() images: string[] = [];
  validatedImageUrl: string | null = null;
  private readonly modalService = inject(ModalService);

  /**
   * Opens the image picker modal gallery.
   * When the user confirms a selection, the chosen base64 string is written to
   * the form control, which triggers the base-class `valueChanges` subscription
   * and ultimately calls `saveFunction` to persist the value.
   */
  async openPicker(): Promise<void> {
    const instance = await this.modalService.open({
      content: ImagePickerModalComponent,
      data: {
        images: this.images,
        currentValue: this.control.value as string | null,
      },
      centered: true,
      size: '720',
    });

    instance.onClose.on((selectedImage: string | null) => {
      if (selectedImage) {
        this.control.setValue(selectedImage);
      }
    });
  }

  /**
   * Deletes the current image by clearing the form control's value and triggering the save pipeline.
   */
  async deleteImage(): Promise<void> {
    this.control.setValue(null);
    await this.onControlValueChanged(null, this.saveFunction);
  }

  /**
   * Returns true if the given value is a base64-encoded data URI (e.g. `data:image/png;base64,...`).
   * Used in the template to conditionally render the `<img>` thumbnail preview.
   *
   * @param {string} value - The form control value to check
   * @returns {boolean} True if the value is a base64 data URI, false otherwise
   */
  isBase64DataUri(value: string): boolean {
    return /^data:[a-z]+\/[a-z0-9+.-]+;base64,/i.test(value ?? '');
  }

  validateImageUrl(url: string) {
    if (!url) {
      this.validatedImageUrl = null;
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      this.validatedImageUrl = null;
      return;
    }

    // Image loading validation
    const img = new Image();
    img.onload = () => {
      this.validatedImageUrl = url;
    };
    img.onerror = () => {
      this.validatedImageUrl = null;
    };
    img.src = url;
  }
}
